import { tenantConfig } from './tenant-config';

const DEFAULT_HEX = '#84cc16';
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function validHex(hex: string | undefined): string {
  if (!hex || !HEX_RE.test(hex)) return DEFAULT_HEX;
  return hex;
}

export function hexToShadeHexes(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const lighten = (c: number, pct: number) => Math.min(255, Math.round(c + (255 - c) * pct));
  const darken = (c: number, pct: number) => Math.round(c * (1 - pct));
  const toHex = (r: number, g: number, b: number) =>
    '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');

  return {
    base: hex,
    light: toHex(lighten(r, 0.2), lighten(g, 0.2), lighten(b, 0.2)),
    dark: toHex(darken(r, 0.2), darken(g, 0.2), darken(b, 0.2)),
    darker: toHex(darken(r, 0.35), darken(g, 0.35), darken(b, 0.35)),
  };
}

export const brandHex = hexToShadeHexes(validHex(tenantConfig.theme.primaryColorHex));

function hexToOklchShades(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l_ = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m_ = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s_ = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l3 = Math.cbrt(l_);
  const m3 = Math.cbrt(m_);
  const s3 = Math.cbrt(s_);

  const L = 0.2104542553 * l3 + 0.7936177850 * m3 - 0.0040720468 * s3;
  const a = 1.9779984951 * l3 - 2.4285922050 * m3 + 0.4505937099 * s3;
  const bOk = 0.0259040371 * l3 + 0.7827717662 * m3 - 0.8086757660 * s3;

  const C = Math.sqrt(a * a + bOk * bOk);
  const h = ((Math.atan2(bOk, a) * 180) / Math.PI + 360) % 360;

  const fmt = (L: number, C: number) =>
    `oklch(${Math.max(0, Math.min(1, L)).toFixed(4)} ${Math.max(0, C).toFixed(4)} ${h.toFixed(2)})`;

  return {
    base: fmt(L, C),
    light: fmt(Math.min(1, L + 0.08), C * 0.95),
    dark: fmt(Math.max(0, L - 0.08), C),
    darker: fmt(Math.max(0, L - 0.15), C),
  };
}

export function injectBrandColors() {
  const hex = validHex(tenantConfig.theme.primaryColorHex);
  if (typeof document === 'undefined') return;

  const shades = hexToOklchShades(hex);
  const root = document.documentElement;

  root.style.setProperty('--brand-color', shades.base);
  root.style.setProperty('--brand-color-light', shades.light);
  root.style.setProperty('--brand-color-dark', shades.dark);
  root.style.setProperty('--brand-color-darker', shades.darker);
}
